<script lang="ts">
  interface SalesPoint {
    label: string;
    actual: number;
    forecast: number;
  }

  let {
    salesData = [],
    monthlyGoal = 500000
  }: {
    salesData: SalesPoint[];
    monthlyGoal: number;
  } = $props();

  const chartParams = $derived.by(() => {
    if (!salesData.length) return { points: '', forecastPoints: '', maxVal: 0, width: 600, height: 180, padding: 20, goalY: 0 };

    const maxVal = Math.max(...salesData.flatMap(d => [d.actual, d.forecast, monthlyGoal]), 10000);
    const width = 600;
    const height = 180;
    const padding = 20;

    const actualPoints = salesData.map((d, index) => {
      const x = padding + (index / (salesData.length - 1)) * (width - padding * 2);
      const y = height - padding - (d.actual / maxVal) * (height - padding * 2);
      return `${x},${y}`;
    }).join(' ');

    const forecastPoints = salesData.map((d, index) => {
      const x = padding + (index / (salesData.length - 1)) * (width - padding * 2);
      const y = height - padding - (d.forecast / maxVal) * (height - padding * 2);
      return `${x},${y}`;
    }).join(' ');

    const goalY = height - padding - (monthlyGoal / maxVal) * (height - padding * 2);

    return { actualPoints, forecastPoints, goalY, maxVal, width, height, padding };
  });
</script>

<div class="p-6 bg-base-100 border border-base-300 rounded-2xl space-y-4">
  <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
    <div>
      <h3 class="text-sm font-bold uppercase tracking-wider text-base-content/70">Sales & Forecast Performance</h3>
      <p class="text-xs text-base-content/50">Comparing actual storefront collections vs. predicted order values</p>
    </div>
    <div class="flex gap-4 text-xs font-semibold">
      <span class="flex items-center gap-1"><span class="w-3 h-0.5 bg-accent"></span> Actual</span>
      <span class="flex items-center gap-1"><span class="w-3 h-0.5 border-t border-dashed border-info"></span> Forecast</span>
      <span class="flex items-center gap-1"><span class="w-3 h-0.5 bg-success"></span> Target</span>
    </div>
  </div>

  <div class="h-56 relative w-full pt-4">
    <svg viewBox="0 0 {chartParams.width} {chartParams.height}" class="w-full h-full overflow-visible">
      <defs>
        <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="oklch(var(--a))" stop-opacity="0.15" />
          <stop offset="100%" stop-color="oklch(var(--a))" stop-opacity="0" />
        </linearGradient>
      </defs>

      {#each [0, 0.5, 1] as ratio}
        {@const y = chartParams.padding + ratio * (chartParams.height - chartParams.padding * 2)}
        <line x1={chartParams.padding} y1={y} x2={chartParams.width - chartParams.padding} y2={y} class="stroke-base-content/10" stroke-width="1" stroke-dasharray="4" />
      {/each}

      <line x1={chartParams.padding} y1={chartParams.goalY} x2={chartParams.width - chartParams.padding} y2={chartParams.goalY} class="stroke-success" stroke-width="1.5" stroke-dasharray="6 4" />

      {#if chartParams.actualPoints}
        <path d="M {chartParams.padding},{chartParams.height - chartParams.padding} L {chartParams.actualPoints} L {chartParams.width - chartParams.padding},{chartParams.height - chartParams.padding} Z" fill="url(#salesGrad)" />
        <polyline fill="none" class="stroke-accent" stroke-width="3" points={chartParams.actualPoints} stroke-linecap="round" stroke-linejoin="round" />
      {/if}

      {#if chartParams.forecastPoints}
        <polyline fill="none" class="stroke-info" stroke-width="2" points={chartParams.forecastPoints} stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="4 3" />
      {/if}
    </svg>
  </div>
</div>
